import { FC, memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { USER_ACCESS_TOKEN } from '@constants/auth';
import { useUser } from '@hooks/api/useUser';
import { useBilling } from '@hooks/common/useBilling';
import VerticalPlanCard from '@pages/Pricing/components/VerticalPlanCard';
import { usePricingContext } from '@pages/Pricing/context';
import {
  HeroSubtitle,
  HeroTitle,
  PlansGrid,
  StyledFreeButton,
  StyledPricingBox,
  ToggleContainer,
} from '@pages/Pricing/styled';
import { RootState } from '@store/store';
import styled from 'styled-components';

import Loading from '@components/Loading/Loading';
import PublicShell from '@components/PublicShell';
import Toggle from '@components/ToggleButtonGroup';

import { useScreenSizeContext } from '@/shared/context/ScreenSizeContext';

const PageContainer = styled.div<{ $isLarger: boolean }>`
  padding: ${props => (props.$isLarger ? '32px' : '16px')};
  max-width: 1400px;
  margin: 0 auto;
`;

const Pricing: FC = () => {
  const { isLoading } = useUser();
  const { t } = useTranslation();
  const { period, setPeriod } = usePricingContext();
  const { config } = useBilling(period);
  const { isLargerScreen } = useScreenSizeContext();

  // Public visitors get a marketing nav + footer; authed users render inside
  // their existing app shell (PrivatePreviewLayout) so we don't double-wrap.
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const hasToken = typeof window !== 'undefined' && !!localStorage.getItem(USER_ACCESS_TOKEN);
  const isLoggedIn = isAuthenticated || hasToken;

  if (isLoading) return <Loading />;

  const body = (
    <PageContainer $isLarger={isLargerScreen}>
      <StyledPricingBox>
        <HeroTitle>
          {t('billing.pricingPage.title')
            .split(' ')
            .map((word, i) => (i === 0 ? <span key={word}>{word} </span> : `${word} `))}
        </HeroTitle>
        <HeroSubtitle>{t('billing.pricingPage.subtitle')}</HeroSubtitle>
        <StyledFreeButton>{t('billing.pricingPage.btnText')}</StyledFreeButton>
      </StyledPricingBox>

      <ToggleContainer>
        <Toggle active={period} setActive={setPeriod} isMobile={!isLargerScreen} />
      </ToggleContainer>

      <PlansGrid>
        {config.map((card, index) => (
          <VerticalPlanCard
            key={card.id}
            isActive={card.isActive}
            isExpired={card.isExpired}
            isPopular={card.isPopular ?? index === 2}
            isFree={card.isFree}
            tokensInPackage={card.tokensInPackage}
            title={card.title}
            subtitle={card.subtitle}
            packagePriceTitle={card.packagePriceTitle}
            packagePriceSubtitle={card.packagePriceSubtitle}
            originalPriceTitle={card.originalPriceTitle}
            annualSavings={card.annualSavings}
            onClick={card.onClick}
            onCancelClick={card.onCancelClick}
            isLoading={card.isLoading}
            packageButtonLocale={card.packageButtonLocale}
            animationDelay={index * 0.08}
          />
        ))}
      </PlansGrid>
    </PageContainer>
  );

  return (
    <>
      <Helmet>
        <title>{t('common.helmetTitles.pricing')}</title>
      </Helmet>
      {isLoggedIn ? body : <PublicShell activeNav="pricing">{body}</PublicShell>}
    </>
  );
};

export default memo(Pricing);
